import { DeleteIcon, EditIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTabelasProcedimentos } from "../hooks/useTabelaProcedimento";
import BadgeTabelaPadrao from "../widgets/BadgeTabelaPadrao";
import EditarTabelaModal from "./EditarTabelaModal";
import ExcluirTabelaDialog from "./ExcluirTabelaDialog";
import ImportarTabelaModal from "./ImportarTabelaModal";

const TabelaProcedimentoList = ({ tipo }) => {
  const navigate = useNavigate();
  const { data: tabelas = [], isLoading } = useTabelasProcedimentos(tipo);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingTabela, setEditingTabela] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const { isOpen: isImportOpen, onOpen: onImportOpen, onClose: onImportClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const filteredTabelas = useMemo(
    () => tabelas.filter((t) => t.nome?.toLowerCase().includes(searchQuery.toLowerCase())),
    [tabelas, searchQuery]
  );

  const handleEditClick = (tabela) => {
    setEditingTabela(tabela);
    onEditOpen();
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    onDeleteOpen();
  };

  return (
    <Box w="full" minW="0">
      <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "stretch", md: "center" }} mb={4} gap={4}>
        <InputGroup w="full" maxW={{ base: "full", md: "320px" }} minW="0">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            aria-label="Buscar tabela"
            placeholder="Buscar tabela..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>
        <Button flexShrink="0" onClick={onImportOpen}>
          Carregar tabela
        </Button>
      </Flex>

      <Stack display={{ base: "flex", md: "none" }} spacing={3}>
        {isLoading ? (
          <Text role="status" color="gray.500">Carregando tabelas...</Text>
        ) : filteredTabelas.length === 0 ? (
          <Text color="gray.500">Nenhuma tabela encontrada.</Text>
        ) : filteredTabelas.map((tabela) => (
          <Box key={tabela._id} borderWidth="1px" borderRadius="md" p={4} minW="0">
            <Stack spacing={2} align="start">
              <Button
                variant="link"
                colorScheme="teal"
                whiteSpace="normal"
                overflowWrap="anywhere"
                textAlign="left"
                maxW="full"
                onClick={() => navigate(`/adm/tabelaprocedimentos/${tabela._id}`)}
              >
                {tabela.nome}
              </Button>
              {tabela.global && <BadgeTabelaPadrao />}
              <Text fontSize="sm">Itens: {tabela.totalItens ?? 0}</Text>
              {!tabela.global && (
                <Flex gap={2} wrap="wrap" pt={1}>
                  <Button size="sm" minH="44px" variant="outline" leftIcon={<EditIcon />} onClick={() => handleEditClick(tabela)}>
                    Editar
                  </Button>
                  <Button size="sm" minH="44px" variant="ghost" colorScheme="red" leftIcon={<DeleteIcon />} onClick={() => handleDeleteClick(tabela._id)}>
                    Excluir
                  </Button>
                </Flex>
              )}
            </Stack>
          </Box>
        ))}
      </Stack>

      <Box display={{ base: "none", md: "block" }} overflowX="auto" maxW="full">
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Nome</Th>
            <Th>Itens</Th>
            <Th textAlign="right">Ações</Th>
          </Tr>
        </Thead>
        <Tbody>
          {isLoading && (
            <Tr><Td colSpan={3}><Text role="status" color="gray.500">Carregando tabelas...</Text></Td></Tr>
          )}
          {!isLoading && filteredTabelas.length === 0 && (
            <Tr>
              <Td colSpan={3}>
                <Text color="gray.500">Nenhuma tabela encontrada.</Text>
              </Td>
            </Tr>
          )}
          {filteredTabelas.map((tabela) => (
            <Tr key={tabela._id} cursor="pointer" _hover={{ bg: "gray.50" }}>
              <Td onClick={() => navigate(`/adm/tabelaprocedimentos/${tabela._id}`)}>
                <Flex gap={2} align="center" wrap="wrap">
                  <Text overflowWrap="anywhere">{tabela.nome}</Text>
                  {tabela.global && <BadgeTabelaPadrao />}
                </Flex>
              </Td>
              <Td onClick={() => navigate(`/adm/tabelaprocedimentos/${tabela._id}`)}>
                {tabela.totalItens ?? 0}
              </Td>
              <Td textAlign="right">
                {!tabela.global && (
                  <HStack justify="flex-end">
                    <IconButton
                      aria-label="Editar"
                      icon={<EditIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditClick(tabela)}
                    />
                    <IconButton
                      aria-label="Excluir"
                      icon={<DeleteIcon />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => handleDeleteClick(tabela._id)}
                    />
                  </HStack>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      </Box>

      <ImportarTabelaModal isOpen={isImportOpen} onClose={onImportClose} tipoPadrao={tipo === "particular" ? "particular" : "convenio"} />
      <EditarTabelaModal isOpen={isEditOpen} onClose={onEditClose} tabela={editingTabela} />
      <ExcluirTabelaDialog isOpen={isDeleteOpen} onClose={onDeleteClose} tabelaId={deleteId} />
    </Box>
  );
};

export default TabelaProcedimentoList;
